"use client";

import { Project, ProjectInfo, SessionPlaylistItem } from "../../types";
import {
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useState,
} from "react";

import { MediaPlayerInstance } from "@vidstack/react";
import { segment } from "../../APITypes/Segment.cs";
import { usePlaybackState } from "./hooks/usePlaybackState";
import { usePlaylistState } from "./hooks/usePlaylistState";
import { useUIState } from "./hooks/useUIState";

interface DataContextProps {
  setScrollPositionY: React.Dispatch<React.SetStateAction<number>>;
  scrollPositionY: number;
  viewportHeight: number;
  setViewportHeight: React.Dispatch<React.SetStateAction<number>>;
  scrollAnimationBegin: number;
  isHeroHovered: boolean;
  setIsHeroHovered: React.Dispatch<React.SetStateAction<boolean>>;
  hoverLeaveTimerRef: React.MutableRefObject<ReturnType<
    typeof setTimeout
  > | null>;
  messageBox: string;
  setMessageBox: React.Dispatch<React.SetStateAction<string>>;
  isMailingListVisible: boolean;
  setIsMailingListVisible: React.Dispatch<React.SetStateAction<boolean>>;
  stoneSubscribeFormStatus: number;
  setStoneSubscribeFormStatus: React.Dispatch<React.SetStateAction<number>>;
  showBusinessCard: boolean;
  setShowBusinessCard: React.Dispatch<React.SetStateAction<boolean>>;
  wasBusinessCardShown: boolean;
  setWasBusinessCardShown: React.Dispatch<React.SetStateAction<boolean>>;
  isBusinessCardPlaying: boolean;
  setIsBusinessCardPlaying: React.Dispatch<React.SetStateAction<boolean>>;
  currentSessionIndex: number;
  setCurrentSessionIndex: React.Dispatch<React.SetStateAction<number>>;
  currentSegmentIndex: number;
  setCurrentSegmentIndex: React.Dispatch<React.SetStateAction<number>>;
  currentSegment: segment | null;
  setCurrentSegment: React.Dispatch<React.SetStateAction<segment | null>>;
  currentPlaylist: SessionPlaylistItem[];
  setCurrentPlaylist: React.Dispatch<
    React.SetStateAction<SessionPlaylistItem[]>
  >;
  currentHighlightIndex: number | null;
  setCurrentHighlightIndex: React.Dispatch<React.SetStateAction<number | null>>;
  startNextSession: () => void;
  startPreviousSession: () => void;
  isHighlightsOnlyMode: boolean;
  setIsHighlightsOnlyMode: (value: boolean) => void;
  videoCanplay: boolean;
  setVideoCanPlay: React.Dispatch<React.SetStateAction<boolean>>;
  isPortal: boolean;
  setIsPortal: React.Dispatch<React.SetStateAction<boolean>>;
  isPaused: boolean;
  setIsPaused: React.Dispatch<React.SetStateAction<boolean>>;
  currentPlaybackTime: number;
  setCurrentPlaybackTime: React.Dispatch<React.SetStateAction<number>>;
  playerInstance: MediaPlayerInstance | null;
  setPlayerInstance: React.Dispatch<
    React.SetStateAction<MediaPlayerInstance | null>
  >;
  autoplayState: boolean;
  setAutoplayState: React.Dispatch<React.SetStateAction<boolean>>;
  skipToTime: number;
  setSkipToTime: React.Dispatch<React.SetStateAction<number>>;
  audioVolumePercent: number;
  setAudioVolumePercent: React.Dispatch<React.SetStateAction<number>>;
  isAudioMuted: boolean;
  setIsAudioMuted: React.Dispatch<React.SetStateAction<boolean>>;
  isPlayerWaiting: boolean;
  setIsPlayerWaiting: React.Dispatch<React.SetStateAction<boolean>>;
  frameForward: () => void;
  frameBack: () => void;
  requestPlayWhenReady: () => void;
  consumePlayWhenReady: () => boolean;
  videoJsOptions: { src: string | undefined; type: string | undefined }[];
  setVideoJsOptions: React.Dispatch<
    React.SetStateAction<
      { src: string | undefined; type: string | undefined }[]
    >
  >;
  isFloatMenuVisible: boolean;
  setIsFloatMenuVisible: React.Dispatch<React.SetStateAction<boolean>>;
  shareMenuOpen: boolean;
  setShareMenuOpen: React.Dispatch<React.SetStateAction<boolean>>;
  navbarFixed: boolean;
  currentProjectInfo: ProjectInfo;
  setCurrentProjectInfo: React.Dispatch<React.SetStateAction<ProjectInfo>>;
  segmentsWatched: string[];
  setSegmentsWatched: React.Dispatch<React.SetStateAction<string[]>>;
  session: SessionPlaylistItem | null;
  firstIndexWithHighlights: number;
  currentAuthorProjects: Project[] | undefined;
  setCurrentAuthorProjects: React.Dispatch<
    React.SetStateAction<Project[] | undefined>
  >;
  isSeeking: boolean;
  setIsSeeking: React.Dispatch<React.SetStateAction<boolean>>;
}

const Context = createContext<DataContextProps | undefined>(undefined);

export const DataProvider = ({ children }: { children: ReactNode }) => {
  const [showBusinessCard, setShowBusinessCard] = useState(false);
  const [wasBusinessCardShown, setWasBusinessCardShown] = useState(false);
  const [isBusinessCardPlaying, setIsBusinessCardPlaying] = useState(false);
  const [currentProjectInfo, setCurrentProjectInfo] = useState<ProjectInfo>(
    {} as ProjectInfo,
  );
  const [currentAuthorProjects, setCurrentAuthorProjects] = useState<
    Project[] | undefined
  >();

  const ui = useUIState();
  const playback = usePlaybackState({ isBusinessCardPlaying });
  const playlist = usePlaylistState({
    playerInstance: playback.playerInstance,
    isPaused: playback.isPaused,
    setIsPaused: playback.setIsPaused,
    isBusinessCardPlaying,
    currentPlaybackTime: playback.currentPlaybackTime,
    isSeeking: playback.isSeeking,
    setIsSeeking: playback.setIsSeeking,
    setSkipToTime: playback.setSkipToTime,
  });

  // Destructure stable setters so effects don't need the whole hook object as a dep
  const {
    setAutoplayState,
    setCurrentPlaybackTime,
    setVideoJsOptions,
    playerInstance,
    isPaused,
  } = playback;
  const {
    currentSessionIndex,
    isHighlightsOnlyMode,
    currentPlaylist,
    setCurrentSegment,
    currentSegmentIndex,
  } = playlist;

  // Reset playback state when session changes
  useEffect(() => {
    const t = setTimeout(() => {
      if (currentSessionIndex === 0) setAutoplayState(false);
      const s = currentPlaylist[currentSessionIndex];
      if (s) {
        setCurrentPlaybackTime(0);
        setCurrentSegment(null);
        if (
          !isPaused &&
          playerInstance?.state.canPlay &&
          !isBusinessCardPlaying
        ) {
          playerInstance.play()?.catch(() => {});
        }
      }
    }, 0);
    return () => clearTimeout(t);
  }, [currentSessionIndex, isHighlightsOnlyMode]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync videoJsOptions whenever playlist, session, or mode changes
  useEffect(() => {
    if (
      isBusinessCardPlaying &&
      !wasBusinessCardShown &&
      currentSessionIndex === 1
    ) {
      setVideoJsOptions([
        {
          src: currentProjectInfo.merchandiseVideoUrl || "",
          type: "application/x-mpegURL",
        },
      ]);
      return;
    }

    const item = currentPlaylist[currentSessionIndex];
    const stream =
      isHighlightsOnlyMode && item?.highlightsOnlyStream?.src
        ? item.highlightsOnlyStream
        : item?.fullStream;

    setVideoJsOptions([
      {
        src: stream?.src || "",
        type: stream?.type || "application/x-mpegURL",
      },
    ]);
  }, [
    currentPlaylist,
    currentSessionIndex,
    currentSegmentIndex,
    isHighlightsOnlyMode,
    isBusinessCardPlaying,
    wasBusinessCardShown,
    currentProjectInfo,
    setVideoJsOptions,
  ]);

  const contextValue: DataContextProps = {
    ...ui,
    ...playback,
    ...playlist,
    showBusinessCard,
    setShowBusinessCard,
    isBusinessCardPlaying,
    setIsBusinessCardPlaying,
    wasBusinessCardShown,
    setWasBusinessCardShown,
    currentProjectInfo,
    setCurrentProjectInfo,
    currentAuthorProjects,
    setCurrentAuthorProjects,
  };

  return <Context.Provider value={contextValue}>{children}</Context.Provider>;
};

export const useDataContext: () => DataContextProps = () => {
  const context = useContext(Context);
  if (!context) {
    throw new Error("useDataContext must be used within a DataProvider");
  }
  return context;
};
